/**
 * Plan cost display. The server is the single source of truth for estimated monthly cost
 * (GET /api/plans/{id}/costs, backend/catalog/cost.py). This module never computes a cost: it fetches the
 * table, looks a tier up, and formats what the server returned. If the server has no table for a plan the
 * screen says so; it does not fall back to arithmetic in the browser (that is how the base charge got
 * counted twice).
 */
import { useEffect, useState } from "react";
import { fetchPlanCosts } from "@/lib/api";

export const COST_TIERS = [500, 1000, 2000];

/** The one place the shape of the costs response is checked. Returns the tier rows or null. */
export function tiersFrom(data) {
  if (!data || !Array.isArray(data.tiers) || data.tiers.length === 0) return null;
  const ok = data.tiers.every((t) => t && Number.isFinite(t.kwh) && Number.isFinite(t.estimated_monthly_cost));
  return ok ? data.tiers : null;
}

/** The server's tier row for a usage level, or null. */
export function tierFor(costs, kwh) {
  const tiers = costs && costs.status === "ok" ? tiersFrom(costs.data) : null;
  return (tiers && tiers.find((t) => t.kwh === kwh)) || null;
}

export function formatDollars(n) {
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "Unavailable";
}

/**
 * "September 20, 2026". A date-only value ("2026-10-01") is read as that calendar day, never shifted by the
 * viewer's time zone; a full timestamp is shown as the viewer's own calendar day.
 */
export function formatLongDate(value) {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/**
 * Loads the server's cost table for one plan. status: "loading" | "ok" | "unavailable".
 * "unavailable" covers a network failure and a plan the server has no complete table for (422); in both
 * cases the UI shows "Unavailable" for the cost, never a number of its own.
 */
export default function usePlanCosts(planId) {
  const [state, setState] = useState({ status: "loading", data: null });
  useEffect(() => {
    if (!planId) return undefined;
    let cancelled = false;
    setState({ status: "loading", data: null });
    Promise.resolve()
      .then(() => fetchPlanCosts(planId))
      .then((data) => { if (!cancelled) setState(tiersFrom(data) ? { status: "ok", data } : { status: "unavailable", data: null }); })
      .catch(() => { if (!cancelled) setState({ status: "unavailable", data: null }); });
    return () => { cancelled = true; };
  }, [planId]);
  return state;
}
